/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, spriteUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkles, Coins } from "lucide-react";

const TYPE_LABEL = {
  capture: "Capture",
  evolution: "Évolution",
  battle: "Combat",
  valuable: "Trésor",
  misc: "Divers",
};

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [money, setMoney] = useState(0);
  const [activeStone, setActiveStone] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [loadingElig, setLoadingElig] = useState(false);
  const [busySell, setBusySell] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inv, me] = await Promise.all([
        api.get("/inventory"),
        api.get("/auth/me"),
      ]);
      setItems(inv.data);
      setMoney(me.data.money || 0);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const openStone = async (stoneId) => {
    setActiveStone(stoneId);
    setLoadingElig(true); setEligible([]);
    try {
      const { data } = await api.get(`/inventory/eligible_pokemon/${stoneId}`);
      setEligible(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingElig(false); }
  };

  const applyStone = async (target) => {
    try {
      const body = { stone_id: activeStone, target_kind: target.kind, ...target };
      delete body.kind;
      const { data } = await api.post("/inventory/use_stone", body);
      toast.success(`Évolution réussie : ${target.name} → ${data.new_name} !`);
      setActiveStone(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sellItem = async (item, qty = 1) => {
    setBusySell(item.item_id);
    try {
      const { data } = await api.post("/shop/sell", { item_id: item.item_id, qty });
      toast.success(`+${data.earned} ₽ — ${item.name} ×${qty} vendu(s).`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusySell(null); }
  };

  return (
    <div data-testid="inventory-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">Sac du Professeur</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1 mb-2">Inventaire</h1>
        </div>
        <div className="panel p-2 px-3 text-right shrink-0">
          <div className="overline text-[9px]">Solde</div>
          <div className="font-serif-display text-2xl leading-none text-[#B87333]" data-testid="prof-money">
            {money} ₽
          </div>
        </div>
      </div>
      <p className="text-xs text-[#4A4A4A] font-mono mb-4 max-w-2xl">
        Les Poké Balls sont consommées à chaque capture réussie. Les pierres d&apos;évolution se trouvent durant les explorations.
        Les <strong>trésors</strong> (Pépite, Perle, Étoile-Lune…) ramenés par les dresseurs se revendent au comptoir du labo.
      </p>

      {items.length === 0 ? (
        <div className="panel p-8 text-center text-[#7A7A7A] font-mono text-sm">
          Sac vide. Envoyez des dresseurs en exploration pour trouver des objets.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="inventory-grid">
          {items.map((it) => (
            <div key={it.item_id} className="panel p-3 flex items-start gap-3" data-testid={`item-${it.item_id}`}>
              <img src={it.sprite} alt={it.name} className="pixelated w-12 h-12 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline">
                  <h3 className="font-serif-display text-xl leading-none">{it.name}</h3>
                  <span className="font-mono text-sm font-bold" data-testid={`item-qty-${it.item_id}`}>×{it.qty}</span>
                </div>
                <div className="overline text-[9px] mt-1">{TYPE_LABEL[it.type] || it.type}</div>
                <p className="text-[11px] text-[#4A4A4A] mt-1 leading-tight">{it.description}</p>
                {it.type === "evolution" && (
                  <button
                    data-testid={`use-stone-${it.item_id}`}
                    className="btn-ink btn-leaf text-[10px] py-1 px-2 mt-2 mr-1"
                    onClick={() => openStone(it.item_id)}
                  >
                    <Sparkles size={10}/> Utiliser
                  </button>
                )}
                {it.sell_value > 0 && (
                  <button
                    data-testid={`sell-${it.item_id}`}
                    className="btn-ink text-[10px] py-1 px-2 mt-2"
                    disabled={busySell === it.item_id}
                    onClick={() => sellItem(it, 1)}
                    title={`Vendre 1 pour ${it.sell_value} ₽`}
                  >
                    <Coins size={10}/> Vendre {it.sell_value}₽
                  </button>
                )}
                {it.sell_value > 0 && it.qty > 1 && (
                  <button
                    data-testid={`sell-all-${it.item_id}`}
                    className="btn-ink text-[9px] py-0.5 px-1.5 mt-1 ml-1"
                    disabled={busySell === it.item_id}
                    onClick={() => sellItem(it, it.qty)}
                    title={`Vendre tout pour ${it.sell_value * it.qty} ₽`}
                  >
                    × tout ({it.sell_value * it.qty}₽)
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeStone && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
          style={{ background: "rgba(26,26,26,0.55)", zIndex: 70 }}
          onClick={() => setActiveStone(null)}
          data-testid="stone-modal"
        >
          <div className="panel-strong p-5 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-3 right-3 btn-ink p-1 px-1.5" onClick={() => setActiveStone(null)}>
              <X size={14}/>
            </button>
            <div className="overline">Évolution par pierre</div>
            <h2 className="font-serif-display text-2xl leading-none mt-1 mb-4">
              Choisir un Pokémon pour la {(items.find(i => i.item_id === activeStone) || {}).name}
            </h2>
            {loadingElig ? (
              <div className="font-mono text-xs text-[#7A7A7A]">Recherche...</div>
            ) : eligible.length === 0 ? (
              <div className="text-xs text-[#7A7A7A] font-mono">
                Aucun Pokémon éligible (ni au PC ni dans les équipes des dresseurs).
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-[400px] overflow-auto">
                {eligible.map((e, i) => (
                  <button
                    key={i}
                    data-testid={`eligible-${i}`}
                    onClick={() => applyStone(e)}
                    className="panel p-2 text-center hover:bg-[#EAE4D9] transition-colors"
                  >
                    <img src={spriteUrl(e.pokemon_id)} alt={e.name} className="pixelated w-14 h-14 mx-auto"/>
                    <div className="font-serif-display text-sm">{e.name}{e.is_shiny ? " ✨" : ""}</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A]">
                      Lv {e.level} · {e.kind === "team" ? `Éq. ${e.trainer_name}` : "PC labo"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
